import { Component, HostListener } from '@angular/core';
import { CalendarEvent, CalendarModule, CalendarView, DateAdapter, DAYS_OF_WEEK } from 'angular-calendar';
import { map, Observable, of, forkJoin, catchError, tap } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConnectServerService } from '../../../../../services/connect-server.service';
import { Day, endOfDay, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter as DateAdapterCore, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS } from '@angular/material-date-fns-adapter';
import { it } from 'date-fns/locale';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { CalendarEventInterface } from '../interfaces/calendar-event-interface';
import { EventInterface } from '../interfaces/event-interface';
import { CalendarType } from './interfaces/calendar-type';
import { EventForCalendar } from './interfaces/event-for-calendar';
import { ApiResponse } from '../../../../../interfaces/api-response';
import { DialogInfoEventComponent } from './components/dialog-info-event/dialog-info-event.component';
import { DialogManageEventComponent } from './components/dialog-manage-event/dialog-manage-event.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { GoogleCalendarInfo, GoogleEvent, GoogleService, googleErrorReason } from '../../../../../services/google.service';
import { DialogGoogleEventComponent } from './components/dialog-google-event/dialog-google-event.component';


const colors: any = {
  red: {
    primary: '#ad2121',
    secondary: '#FAE3E3',
  },
  blue: {
    primary: '#1e90ff',
    secondary: '#D1E8FF',
  },
  yellow: {
    primary: '#e3bc08',
    secondary: '#FDF1BA',
  },
  violet: {
    primary: '#9306b2',
    secondary: '#f8dffe',
  },
};

@Component({
    selector: 'app-calendar',
    imports: [
        CommonModule,
        CalendarModule,
        MatIconModule,
        MatButtonModule,
        TranslateModule,
        RouterLink,
    ],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: it },
        { provide: DateAdapterCore, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } }
    ],
    templateUrl: './calendar.component.html',
    styleUrl: './calendar.component.scss'
})
export class CalendarComponent {
  view: CalendarView = CalendarView.Week;
  viewDate: Date = new Date();
  locale = 'it';
  weekStartsOn: Day = DAYS_OF_WEEK.MONDAY as Day;
  weekendDays: number[] = [DAYS_OF_WEEK.SATURDAY, DAYS_OF_WEEK.SUNDAY];
  dayStartHour = 7;
  dayEndHour = 21;
  arrayCalendarioTipo: CalendarType[] = [];
  CalendarView = CalendarView;
  activeDayIsOpen = false;
  events$!: Observable<CalendarEvent[]>;
  googleCalendars: GoogleCalendarInfo[] = [];
  googleConnected = false;
  googleNeedsReauth = false;
  googleWarning = '';
  eventoModifica!: CalendarEventInterface;
  dialogConfig = new MatDialogConfig();

  constructor(private dialog: MatDialog,
    private connectService: ConnectServerService,
    private googleService: GoogleService,
    private translate: TranslateService) {

    this.dialogConfig.disableClose = false;
    this.dialogConfig.autoFocus = true;
    this.dialogConfig.data = null;
    this.dialogConfig.panelClass = '';
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.checkScreenSize();
  }

  checkScreenSize(): void {
    const screenWidth = window.innerWidth;
    if (screenWidth < 768) {
      this.view = CalendarView.Month;
    } else {
      this.view = CalendarView.Week;
    }
  }

  ngOnInit(): void {
    this.checkScreenSize();
    this.googleService.status().subscribe({
      next: (status) => {
        this.googleConnected = status.connected === 1;
        this.googleNeedsReauth = status.status === 'reauth_required';
        this.loadCalendarTypes();
      },
      error: () => this.loadCalendarTypes(),
    });
  }

  private loadCalendarTypes(): void {
    this.connectService.getRequestCookie<CalendarType>(
      'agenda/calendarTypes', {})
      .subscribe(
        (arrayCalendarioTipo: CalendarType[]) => {
          this.arrayCalendarioTipo = arrayCalendarioTipo;
          if (!this.googleConnected) {
            this.fetchEvents();
            return;
          }
          this.googleService.calendars().subscribe({
            next: (calendars) => {
              this.googleCalendars = calendars;
              this.fetchEvents();
            },
            error: (error: unknown) => {
              this.googleWarning = this.translate.instant('CALENDAR.GOOGLE.REASON.' + googleErrorReason(error));
              this.fetchEvents();
            },
          });
        }
      );
  }

  /**
   * Per ogni evento
   * @param evento
   */
  private elaboraEvento(evento: EventForCalendar): CalendarEvent {
    const obj: CalendarEvent = {
      start: new Date(evento.date_start),
      title: evento.title
    };
    // console.log('evento', evento);
    const code = this.arrayCalendarioTipo.find(x => x.code == evento.code)?.code;
    obj.color = colors.blue;
    if (code == 'ROOM') {
      obj.color = colors.violet;
    } else if (code == 'REMINDER') {
      obj.color = colors.yellow;
    } else if (code == 'GENERIC') {
      // obj.color = colors.red;
    } else if (code == 'COURSE') {
      const colore: any = {
        personalizzato: {
          primary: `${evento.color_hex}`,
          secondary: `${evento.color_hex}`,
        }
      };;
      obj.color = colore.personalizzato;
    }
    if (evento.isallday === 1) {
      obj.allDay = true;
      obj.start = new Date(evento.date_start);
      if(evento.date_end){
        obj.end = new Date(evento.date_end);
      }
    } else {
      obj.allDay = false;
      const arr_dataini = String(evento.date_start).split('-');
      if (evento && evento.time_start && evento.time_end) {
        const arr_oraini = evento.time_start.split(':');
        obj.start = new Date(+arr_dataini[0], +arr_dataini[1] - 1, +arr_dataini[2], +arr_oraini[0], +arr_oraini[1], +arr_oraini[2]);
        const arr_datafine = String(evento.date_end).split('-');
        const arr_orafine = evento.time_end.split(':');
        obj.end = new Date(+arr_datafine[0], +arr_datafine[1] - 1, +arr_datafine[2], +arr_orafine[0], +arr_orafine[1], +arr_orafine[2]);
        obj.resizable = {
          beforeStart: false,
          afterEnd: false
        };
      }

    }
    obj.id = evento.id;
    obj.meta = {
      source: 'walley',
      evento
    };
    return obj;
  }

  private elaboraGoogle(evento: GoogleEvent): CalendarEvent {
    const color = evento.color || '#1e90ff';
    const obj: CalendarEvent = {
      id: `${evento.calendar_id}:${evento.id}`,
      title: evento.title || this.translate.instant('CALENDAR.GOOGLE.UNTITLED'),
      start: new Date(evento.date_start),
      allDay: evento.is_all_day === 1,
      color: { primary: color, secondary: color },
      meta: { source: 'google', google: evento },
    };
    if (evento.is_all_day === 1) {
      if (evento.date_end) {
        obj.end = new Date(evento.date_end);
      }
      return obj;
    }
    if (evento.time_start && evento.time_end) {
      const startDate = evento.date_start.split('-');
      const startTime = evento.time_start.split(':');
      obj.start = new Date(+startDate[0], +startDate[1] - 1, +startDate[2], +startTime[0], +startTime[1]);
      const endDate = evento.date_end.split('-');
      const endTime = evento.time_end.split(':');
      obj.end = new Date(+endDate[0], +endDate[1] - 1, +endDate[2], +endTime[0], +endTime[1]);
    }
    return obj;
  }
  /*private startW(){
      return startOfWeek(this.viewDate, {weekStartsOn: 1});
  }
  private endW(){
      return endOfWeek(new Date().getTime(), {weekStartsOn: 1});
  }*/
  /**
   * La funzione si collega al server ed in base alla data carica gli eventi
   */
  fetchEvents(): void {
    const getStart: Record<CalendarView, (date: Date) => Date> = {
      [CalendarView.Month]: (date: Date) => startOfMonth(date),
      [CalendarView.Week]: (date: Date) => startOfWeek(date, { weekStartsOn: this.weekStartsOn }),
      [CalendarView.Day]: (date: Date) => startOfDay(date),
    } as Record<CalendarView, (date: Date) => Date>;

    const getEnd: Record<CalendarView, (date: Date) => Date> = {
      [CalendarView.Month]: (date: Date) => endOfMonth(date),
      [CalendarView.Week]: (date: Date) => endOfWeek(date, { weekStartsOn: this.weekStartsOn }),
      [CalendarView.Day]: (date: Date) => endOfDay(date),
    } as Record<CalendarView, (date: Date) => Date>;

    const startDate = getStart[this.view](this.viewDate);
    const endDate = getEnd[this.view](this.viewDate);
    const startdate = format(startDate, 'yyyy-MM-dd');
    const enddate = format(endDate, 'yyyy-MM-dd');

    const walley$ = this.connectService.getRequestCookie('agenda/eventsListCalendar', { startdate, enddate }).pipe(
      map((results: ApiResponse<EventForCalendar[]>) => (results.data ?? []).map((evento: EventForCalendar) => this.elaboraEvento(evento))),
      catchError(() => of([] as CalendarEvent[])),
    );
    const google$ = this.googleConnected
      ? this.googleService.events(startdate, enddate).pipe(
        tap(() => {
          this.googleWarning = '';
        }),
        map((events) => events.map((evento) => this.elaboraGoogle(evento))),
        catchError((error: unknown) => {
          this.googleWarning = this.translate.instant('CALENDAR.GOOGLE.REASON.' + googleErrorReason(error));
          return of([] as CalendarEvent[]);
        }),
      )
      : of([] as CalendarEvent[]);

    this.events$ = forkJoin([walley$, google$]).pipe(
      map(([walley, google]) => [...walley, ...google]),
    );
  }

  toggleGoogleCalendar(calendar: GoogleCalendarInfo, checked: boolean): void {
    const previous = calendar.visible;
    calendar.visible = checked ? 1 : 0;
    const ids = this.googleCalendars.filter((item) => item.visible === 1).map((item) => item.id);
    this.googleService.saveVisibleCalendars(ids).subscribe({
      next: (calendars) => {
        this.googleCalendars = calendars;
        this.googleWarning = '';
        this.fetchEvents();
      },
      error: () => {
        calendar.visible = previous;
      },
    });
  }

  /**
   * Chiamata quando si clicca su mese, settimana o giorno
   * @param view
   */
  setView(view: CalendarView): void {
    this.view = view;
    this.fetchEvents();
  }

  /**
   * Quando clicco sul giorno nella visualizzazione a mese
   * @param date
   * @param events
   */
  dayClicked({
    date, events,
  }: {
    date: Date;
    events: CalendarEvent<{ evento: EventInterface }>[];
  }): void {
    if (isSameMonth(date, this.viewDate)) {
      if (
        (isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) ||
        events.length === 0
      ) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
        this.viewDate = date;
      }
    }
  }

  /**
   * La funzione aggiunge un nuovo evento
   */
  addEvento(): void {
    // const dataNow = this.datePipe.transform(day, 'dd/MM/yyyy');

    // dialogConfig.backdropClass = 'pippo';
    this.editOrCreateEventPopup();

  }


  addGoogleEvent(): void {
    const dialogRef = this.dialog.open(DialogGoogleEventComponent, {
      maxWidth: '900px',
      minWidth: '350px',
      maxHeight: '800px',
      width: '90%',
      data: { mode: 'create', calendars: this.googleCalendars },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action === 'refresh') {
        this.fetchEvents();
      }
    });
  }

  private editOrCreateEventPopup(idevent: number = 0) {
    const dialogRef = this.dialog.open(DialogManageEventComponent, {
      maxWidth: '900px',
      minWidth: '350px',
      maxHeight: '800px',
      width: '90%',
      data: { calendar_type: ['GENERIC', 'COURSE', 'ROOM', 'REMINDER'], idevent: idevent }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result != null && result.action == 'refresh') {
        this.fetchEvents();
      }
    });
  }
  /**
   * Viene chiamato quando si clicca su un evento, fa vedere la card
   * @param event
   */
  handleEvent(event: CalendarEvent): void {
    const googleEvent = event.meta?.google as GoogleEvent | undefined;
    if (event.meta?.source === 'google' && googleEvent) {
      const dialogRef = this.dialog.open(DialogGoogleEventComponent, {
        maxWidth: '900px',
        minWidth: '350px',
        maxHeight: '800px',
        width: '90%',
        data: {
          mode: googleEvent.writable === 1 ? 'edit' : 'view',
          calendars: this.googleCalendars,
          event: googleEvent,
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result?.action === 'refresh') {
          this.fetchEvents();
        }
      });
      return;
    }

    const obj_evento: EventForCalendar = event.meta.evento;
    if (obj_evento.id > 0) {
      const dialogRef = this.dialog.open(DialogInfoEventComponent,
        {
          maxWidth: '900px',
          minWidth: '350px',
          maxHeight: '800px',
          width: '90%',
          data: { idevent: obj_evento.id }
        }
      );
      dialogRef.afterClosed().subscribe(
        (result) => {
          if (result != null) {
            if (result.action == 'edit' && result.idevent > 0) {
              this.editOrCreateEventPopup(result.idevent);
            }
          }
        }
      );
    }
    // this.modal.open(this.modalContent, { size: 'lg' });
  }
}
